import React, { useState, useEffect, useContext, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import PropTypes from 'prop-types';
import { contactRoles, errors, fields, personObj, personTitles, emails, urls } from '../../../constants';
import { personEntityTitle } from '../../../utils/helper';
import { isOfficerComplete } from '../../../utils/completes';
import { ProfileContext } from '../../../context/Profile.context';
import Controls from '../../Controls';
import PrivateInformation from '../../PrivateInformation/PrivateInformation';
import PreviousAdded from '../../PreviousAdded';
import RemovePerson from '../../RemovePerson';
import SortableDrag from '../../SortableDrag';
import Title from '../../Title';
import TitleSelect from '../../TitleSelect';

import { Check, CircleAlert, Lock, GripVertical, User, UserCheck, UserLock, Trash2 } from 'lucide-react';
import { Box, Button, Dialog, DialogContent, DialogTitle, EntityLabelGroup, EntityLabel, Label, TextField, Typography, InfoPanel, SectionTitle, Divider, Tooltip } from '@otcmarketsgroup/otcm-ui-lib';
import { PhoneInputField } from '../../PhoneInputField';

const PREV_ADDED_VIEW = 'previousAdded';
const FORM_VIEW = 'formView';
const REMOVE_PERSON_VIEW = 'removePerson';
const isOfficer = fields.OFFICER;
const isNotPublish = fields.DO_NOT_PUBLISH;
const DO_NOT_PUBLISH = <p>The following contacts are not publicly displayed on your Company Profile on <a href={urls.HOME} target='_blank' rel='noopener noreferrer'>OTCMarkets.com</a>. If you would like to change this designation, please contact Issuer Services at <a href={emails.ISSUER_SERVICES}>issuers@otcmarkets.com</a> or call 212-896-4420. Note that it is OTC Markets Group policy to display all Company Officers, Directors and Control Persons.</p>;

const OfficersTab = ({ onPersonAdd, onPersonUpdate, onPersonRemove }) => {
  const [profileState, dispatchProfile] = useContext(ProfileContext);
  const [dialog, setDialog] = useState(false);
  const [officerView, setOfficerView] = useState(personObj);
  const [addTitle, toggleAddTitle] = useState(false);
  const [validationError, setValidationError] = useState(false);
  const [titles, setTitles] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState(null);
  const [viewHistory, setViewHistory] = useState(null);
  const titleSelectContainerRef = useRef(null);
  const companyName = profileState.profile.companyInfo.companyName;
  const peopleList = profileState.profile.peopleList;
  const directorList = peopleList && peopleList.filter(person => person.isDirector && !person[isOfficer]);
  const previousAddView = directorList && directorList.length > 0;
  const [slideView, setSlideView] = useState(previousAddView ? PREV_ADDED_VIEW : FORM_VIEW);
  const isMaxTitles = officerView && officerView.title4;
  const dnpOfficerList = (peopleList && peopleList.filter(person => person[isOfficer] && person[isNotPublish])) || [];
  const hasDNPOfficerList = dnpOfficerList.length > 0;
  const officerList = peopleList && peopleList.filter(person => person[isOfficer] && !person[isNotPublish]).sort((a, b) => {
    const nameA = a.title1 || '';
    const nameB = b.title1 || '';

    const indexA = personTitles.indexOf(nameA);
    const indexB = personTitles.indexOf(nameB);

    if (indexA === -1 && indexB === -1) {
      // If both names are not in the personTitles, sort them alphabetically.
      return nameA.localeCompare(nameB);
    } else if (indexA === -1) {
      // If only nameA is not in the personTitles, it comes after nameB.
      return 1;
    } else if (indexB === -1) {
      // If only nameB is not in the personTitles, it comes before nameA.
      return -1;
    }

    // Compare the indexes in personTitles to determine the sorting order.
    return indexA - indexB;
  });
  const hasTitles = officerView && officerView.title1;
  const isAdd = !officerView.id || officerView && !officerView[isOfficer];
  const isReadOnly = profileState.readOnly;
  const isRemoveView = slideView === REMOVE_PERSON_VIEW;
  const dialogTitle = isRemoveView ? `Remove - ${personEntityTitle(officerView, false)}` : isAdd ? 'Add Officer or Contact' : personEntityTitle(officerView, false);
  const hasOfficers = officerList && officerList.length > 0 || hasDNPOfficerList;

  useEffect(() => {
    if (!dialog) {
      setViewHistory(null);
      setOfficerView(personObj);
      setSlideView(previousAddView ? PREV_ADDED_VIEW : FORM_VIEW);
      setValidationError(false);
      setSelectedTitle(null);
      toggleAddTitle(false);
    }
  }, [dialog, previousAddView]);

  useEffect(() => {
    let titleArray = [];
    if (officerView && officerView.title1) titleArray.push(officerView.title1);
    if (officerView && officerView.title2) titleArray.push(officerView.title2);
    if (officerView && officerView.title3) titleArray.push(officerView.title3);
    if (officerView && officerView.title4) titleArray.push(officerView.title4);
    setTitles(titleArray);
  }, [officerView]);

  useEffect(() => {
    if (!addTitle) return;

    const handleClickAway = event => {
      const clickedInsideTitleContainer = titleSelectContainerRef.current && titleSelectContainerRef.current.contains(event.target);
      const clickedInsideDropdownOverlay = event.target instanceof Element
        && (
          event.target.closest('.MuiAutocomplete-popper')
          || event.target.closest('[role="listbox"]')
          || event.target.closest('[role="option"]')
        );

      if (!clickedInsideTitleContainer && !clickedInsideDropdownOverlay) {
        toggleAddTitle(false);
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    document.addEventListener('touchstart', handleClickAway);

    return () => {
      document.removeEventListener('mousedown', handleClickAway);
      document.removeEventListener('touchstart', handleClickAway);
    };
  }, [addTitle]);

  const onValueChanged = (field, e) => {
    setOfficerView(prevState => ({
      ...prevState,
      [field]: e.target.value
    }));
  };

  const onPhoneValueChanged = (phone, data) => {
    setOfficerView(prevState => ({
      ...prevState,
      phone: phone,
      phoneCountryCode: data.countryCode.toUpperCase()
    }));
  };

  const onRemoveSubmit = person => {
    onPersonUpdate(person);
    dialogClose();
  };

  const handleEditView = person => {
    let titleArray = [];
    if (person.title1) titleArray.push(person.title1);
    if (person.title2) titleArray.push(person.title2);
    if (person.title3) titleArray.push(person.title3);
    if (person.title4) titleArray.push(person.title4);
    setSlideView(FORM_VIEW);
    setOfficerView({ ...person, titles: titleArray });
    setDialog(true);
  };

  const handleCancelView = _ => {
    viewHistory === PREV_ADDED_VIEW && directorList && directorList.length > 0 ? setSlideView(PREV_ADDED_VIEW) : dialogClose();
    viewHistory === PREV_ADDED_VIEW && setOfficerView(personObj);
  };

  const handleSubmitPerson = _ => {
    setValidationError(false);

    const officer = officerView;
    const onSubmit = !officer.id ? onPersonAdd : onPersonUpdate;

    officer[isOfficer] = true;

    officer.title1 = titles[0];
    officer.title2 = titles[1];
    officer.title3 = titles[2];
    officer.title4 = titles[3];

    if (!isOfficerComplete(officer)) {
      setValidationError(true);
      return;
    }

    delete officer.titles;

    onSubmit(officer);
    dialogClose();
  };

  const handleRemoveClick = (e, person) => {
    // e.stopPropagation();
    setOfficerView(person);
    setSlideView(REMOVE_PERSON_VIEW);
    setDialog(true);
  };

  const isCustomRemove = person => {
    const totalRoles = contactRoles.filter(role => person[role]);

    return totalRoles.length > 1;
  };

  const handleCancelRemove = _ => {
    dialogClose();
  };

  const validateComplete = person => {
    return isOfficerComplete(person);
  };

  const onTitleRemove = title => {
    const updateTitles = officerView.titles;
    const index = updateTitles.indexOf(title);
    updateTitles.splice(index, 1);

    setOfficerView(prevState => {
      return {
        ...prevState,
        titles: updateTitles,
        title1: updateTitles[0],
        title2: updateTitles[1],
        title3: updateTitles[2],
        title4: updateTitles[3]
      };
    });
  };


  const handleTitleSave = value => {
    setOfficerView(prevState => {
      const titleList = [...titles || [], value];
      setTitles(titleList);
      return {
        ...prevState,
        titles: titleList,
        title1: titleList[0],
        title2: titleList[1],
        title3: titleList[2],
        title4: titleList[3]
      };
    });

    setSelectedTitle(null);
    toggleAddTitle(false);
  };

  const renderTitles = _ => (
    <DndProvider backend={HTML5Backend}>
      <EntityLabelGroup>{titles && titles.map((card, i) => {
        const onRemove = () => onTitleRemove(card);

        return (
          (
            <SortableDrag key={`${card}${i}`} index={i} id={card} items={titles} setItems={setTitles} disable={isReadOnly}>
              <EntityLabel
                title={card}
                isComplete
                icon={!isReadOnly ? <GripVertical size={16} color="var(--otc-medium-gray)" /> : undefined}
                actionIcon={!isReadOnly ? <Trash2 /> : undefined}
                onActionClick={!isReadOnly ? onRemove : undefined} />
            </SortableDrag>
          )
        );
      })}</EntityLabelGroup>
    </DndProvider>
  );

  const renderAddNew = () => {
    const titleInvalid = validationError && !officerView?.title1;

    return <>
      <DialogContent>
        <Box display='flex' flexDirection='column' gap={2}>
          <PrivateInformation />
          <Box display='flex' flexDirection='column' gap={2}>
            <TextField
              label="First Name"
              placeholder="Enter text here"
              showClearButton
              maxLength={75}
              value={officerView?.firstName || ''}
              readOnly={isReadOnly}
              error={(validationError || isReadOnly) && !officerView?.firstName}
              helperText={(validationError || isReadOnly) && !officerView?.firstName ? errors.INCOMPLETE_DATA_FIELD : ''}
              onChange={(event) => onValueChanged('firstName', event)} />
            <TextField
              label="Middle Name"
              placeholder="Enter text here"
              maxLength={75}
              showClearButton
              value={officerView?.middleName}
              readOnly={isReadOnly}
              onChange={(event) => onValueChanged('middleName', event)} />
            <TextField
              label="Last Name"
              placeholder="Enter text here"
              maxLength={75}
              showClearButton
              value={officerView?.lastName || ''}
              readOnly={isReadOnly}
              error={(validationError || isReadOnly) && !officerView?.lastName}
              helperText={(validationError || isReadOnly) && !officerView?.lastName ? errors.INCOMPLETE_DATA_FIELD : ''}
              onChange={(event) => onValueChanged('lastName', event)} />
            <Box>
              <Label startIcon={<Lock size="16px" color="var(--otc-green)" />}>
                Phone Number
              </Label>
              <PhoneInputField
                value={officerView?.phone}
                country={officerView?.phoneCountryCode?.toLowerCase()}
                disabled={isReadOnly}
                onChange={onPhoneValueChanged} />
            </Box>
            <Box>
              <Label startIcon={<Lock size="16px" color="var(--otc-green)" />}>
                Email
              </Label>
              <TextField
                placeholder="Enter text here"
                showClearButton
                maxLength={75}
                value={officerView?.email}
                readOnly={isReadOnly}
                onChange={(event) => onValueChanged('email', event)} />
            </Box>
          </Box>
          <div ref={titleSelectContainerRef}>
            <div>
              <Tooltip dark disableHoverListener={!titleInvalid} subtitle={errors.INCOMPLETE_DATA_FIELD} placement='top-start'>
                <Box sx={{ borderBottom: titleInvalid ? '3px solid' : 'none', borderColor: 'var(--otc-warning)', mb: titleInvalid ? 1 : 0 }}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {titleInvalid && <CircleAlert color='var(--otc-warning)' size={16} />}
                      <Title type='h3' title='Titles' />
                    </Box>
                    {(!isMaxTitles && !isReadOnly) && <Button variant='text'
                      onClick={() => toggleAddTitle(true)}>
                      Add Title
                    </Button>}
                  </Box>
                </Box>
              </Tooltip>
              {addTitle && <Box >
                <TitleSelect
                  readOnly={isReadOnly}
                  onSelection={handleTitleSave}
                  selectedTitles={titles} />
              </Box>}
              <Typography variant='body1' gutterBottom>
                Please enter all titles associated with this person.
              </Typography>
              {hasTitles && renderTitles()}
              {isMaxTitles && <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, mt: 1 }}>
                <Check size={12} color="var(--otc-medium-gray)" />
                <Typography variant='body2'>
                  Maximum number of titles reached.
                </Typography>
              </Box>}
              {((officerView && !officerView.title1) && isReadOnly) && <Typography variant='body2' sx={{ color: 'var(--otc-warning)' }}>
                No Titles
              </Typography>}
            </div>
          </div>
          <Box display='flex' flexDirection='column' gap={2}>
            <TextField
              label="LinkedIn Profile"
              placeholder="Enter text here"
              showClearButton
              value={officerView?.linkedInId}
              maxLength={75}
              readOnly={isReadOnly}
              onChange={(event) => onValueChanged('linkedInId', event)} />
            <TextField
              label="X Profile"
              placeholder="Enter text here"
              showClearButton
              maxLength={75}
              value={officerView?.twitterId}
              readOnly={isReadOnly}
              onChange={(event) => onValueChanged('twitterId', event)} />
            <TextField
              label="Facebook Profile"
              placeholder="Enter text here"
              showClearButton
              maxLength={75}
              value={officerView?.facebookId}
              readOnly={isReadOnly}
              onChange={(event) => onValueChanged('facebookId', event)} />
            <Box>
              <TextField
                label="Biography"
                placeholder="Enter text here"
                multiline
                showCharCounter
                maxLength={1500}
                value={officerView?.biography}
                readOnly={isReadOnly}
                onChange={(event) => onValueChanged('biography', event)} />
            </Box>
          </Box>
          {validationError && <InfoPanel isWarning title={errors.INCOMPLETE_DATA} />}
        </Box>
      </DialogContent>;
      {!isReadOnly && <Controls
        className='mtXXL'
        cancelText='Cancel'
        submitText={isAdd ? 'Add' : 'Update'}
        onCancelClick={handleCancelView}
        onSubmitClick={handleSubmitPerson}
      />}
    </>
  };

  const handleSelectDirector = person => {
    const newOfficer = { ...person };
    if (!newOfficer.titles) newOfficer.titles = [];
    setViewHistory(PREV_ADDED_VIEW);
    setOfficerView(newOfficer);
    setSlideView(FORM_VIEW);
  };

  const handleAddNew = _ => {
    setViewHistory(PREV_ADDED_VIEW);
    setSlideView(FORM_VIEW);
  };

  const renderPreviouslyAdded = () => (
    <DialogContent>
      <Box>
        <p>
          Select individual previously entered as a Director or add a new individual below
        </p>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PreviousAdded
            listTitle='All Directors Previously Added'
            items={directorList}
            handleSelect={handleSelectDirector} />
          <Divider />
          <Button
            fullWidth
            onClick={handleAddNew}>
            Add New Officer or Contact
          </Button>
        </Box>
      </Box>
    </DialogContent>
  );

  const dialogClose = _ => {
    setDialog(false);
  };

  return (<>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!hasOfficers && <InfoPanel isWarning title={`At least one Officer is required`} />}
      <Typography variant='body1'>
        Enter all Executive Officers and Key Contacts to be included on the Company Profile for {companyName} and provide contact information.
      </Typography>
      <Box>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <SectionTitle noUnderline>
            Executive Officers and Key Contacts
          </SectionTitle>
          {!isReadOnly && <Button variant='text' onClick={() => setDialog(true)}>
            Add Officer or Contact
          </Button>}
        </Box>
        {!hasOfficers && <Typography variant='body2'>
          No Officers have been added yet.
        </Typography>}
        <EntityLabelGroup>
          {officerList.map((officer, i) => {
            const isComplete = validateComplete(officer);

            return (
              <EntityLabel
                key={i}
                title={personEntityTitle(officer)}
                subtitle={isComplete ? 'Complete' : 'Enter Information'}
                icon={isComplete ? <UserCheck /> : <User />}
                actionIcon={!isReadOnly ? <Trash2 /> : undefined}
                isComplete={isComplete}
                isWarning={!isComplete}
                onEntityClick={() => handleEditView(officer)}
                onActionClick={!isReadOnly ? (e) => handleRemoveClick(e, officer) : undefined} />
            );
          })}
        </EntityLabelGroup>
      </Box>

      {hasDNPOfficerList && <Box>
        {DO_NOT_PUBLISH}
        <EntityLabelGroup>
          {dnpOfficerList.map((contact, i) => {
            const isComplete = validateComplete(contact);

            return (
              <EntityLabel
                key={i}
                title={personEntityTitle(contact)}
                subtitle={isComplete ? 'Complete' : 'Enter Information'}
                icon={<UserLock />}
                actionIcon={!isReadOnly ? <Trash2 /> : undefined}
                isComplete={isComplete}
                onEntityClick={() => handleEditView(contact)}
                onActionClick={!isReadOnly ? (e) => handleRemoveClick(e, contact) : undefined} />
            );
          })}
        </EntityLabelGroup>
      </Box>
      }
    </Box>

    <Dialog open={dialog} onClose={dialogClose}>
      <DialogTitle title={dialogTitle} onClose={dialogClose} />
      {slideView === PREV_ADDED_VIEW && renderPreviouslyAdded()}
      {slideView === FORM_VIEW && renderAddNew()}
      {isRemoveView && <RemovePerson
        person={{ ...officerView }}
        selectedRemove={isOfficer}
        onCancel={handleCancelRemove}
        onRemoveSubmit={onRemoveSubmit} />}
    </Dialog>
  </>
  );
};

OfficersTab.propTypes = {
  onPersonAdd: PropTypes.func,
  onPersonUpdate: PropTypes.func,
  onPersonRemove: PropTypes.func
};


export default OfficersTab;
